import React, { useState } from 'react';
// import { ReactComponent as TokenIcon } from '../assets/svg/vlr_token_icon.svg';
import { ReactComponent as TokenIcon } from '../assets/svg/token_image.svg'; //
import { Campaign } from '../App';
import { PrimaryButton, SecondaryButton } from './Button';
import { Tooltip } from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import { getTimeRemaining, formatExactDateTime } from '../utils/dateUtils';

interface CampaignRowProps {
  campaign: Campaign;
  type?: 'ongoing' | 'previous';
}

const CampaignRow: React.FC<CampaignRowProps> = ({ campaign, type = 'previous' }) => {
  const { theme } = useTheme();
  const { title, status, statusColor, rewards, buttonText, dateEnd, rewardsText, tooltip } =
    campaign;

  // Format the end date using dateUtils
  const timeRemaining = dateEnd ? getTimeRemaining(dateEnd) : '-';
  const exactDateTime = dateEnd ? formatExactDateTime(dateEnd, false) : '';
  const [isHovering, setIsHovering] = useState(false);

  // Ensure Tailwind includes these classes
  const getStatusColorClass = (color: string): string => {
    const colorMap: { [key: string]: string } = {
      'text-secondary-orange': 'text-secondary-orange',
      'text-secondary-shamrock': 'text-secondary-shamrock',
    };
    return colorMap[color] || color;
  };

  const handleButtonClick = (): void => {
    window.open(campaign.link, '_blank');
  };

  return (
    <div
      className="bg-white dark:bg-contrast-2 hover:bg-[#FDFDFD] dark:hover:bg-contrast-3 border border-contrast-7 dark:border-transparent transition-colors rounded-lg p-4 md:p-6"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col gap-3">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-contrast-5 text-xs font-inter mb-1">Campaign</div>
            <div className="text-contrast-3 dark:text-white text-sm font-medium font-inter">
              {title}
            </div>
          </div>
          {type === 'ongoing' ? (
            <PrimaryButton onClick={handleButtonClick}>{buttonText}</PrimaryButton>
          ) : (
            <SecondaryButton onClick={handleButtonClick}>{buttonText}</SecondaryButton>
          )}
        </div>
        <div className="flex justify-between items-center">
          <div>
            <div className="text-contrast-5 text-xs font-inter mb-1">Status</div>
            <div className={`text-sm font-inter ${getStatusColorClass(statusColor)}`}>{status}</div>
          </div>
          <div>
            <div className="text-contrast-5 text-xs font-inter mb-1">
              {type === 'ongoing' ? 'Ends in' : 'Rewards'}
            </div>
            {type === 'ongoing' ? (
              <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono cursor-default">
                {isHovering ? exactDateTime : timeRemaining}
              </div>
            ) : rewards ? (
              <div className="flex items-center gap-2">
                <TokenIcon className="w-4 h-4 flex-shrink-0" />
                <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono">
                  {rewards}
                </div>
              </div>
            ) : (
              <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono">
                -
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:grid gap-6 items-center grid-cols-[2fr_1fr_1.5fr_auto]">
        {/* Campaign Column */}
        <div>
          <div className="text-contrast-5 text-xs font-inter mb-2">Campaign</div>
          <div className="text-contrast-3 dark:text-white text-sm font-medium font-inter">
            {tooltip ? (
              <Tooltip
                arrow
                title={tooltip}
                placement="top-start"
                // leaveDelay={100000}
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      lineHeight: '18px',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      maxWidth: 300,
                      backgroundColor: theme === 'dark' ? '#f5f5f5' : '#333',
                      color: theme === 'dark' ? '#222' : '#fff',
                    },
                  },
                  arrow: {
                    sx: {
                      color: theme === 'dark' ? '#f5f5f5' : '#333',
                    },
                  },
                }}
              >
                <span>{title}</span>
              </Tooltip>
            ) : (
              title
            )}
          </div>
        </div>

        {/* Status Column */}
        <div>
          <div className="text-contrast-5 text-xs font-inter mb-2">Status</div>
          <div className={`text-sm font-inter ${getStatusColorClass(statusColor)}`}>{status}</div>
        </div>

        {/* Rewards Column - Show for all campaigns */}
        <div>
          <div className="text-contrast-5 text-xs font-inter mb-2">
            {type === 'ongoing' ? 'Ends in' : 'Rewards'}
          </div>
          {type === 'ongoing' ? (
            <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono cursor-default">
              {isHovering ? exactDateTime : timeRemaining}
            </div>
          ) : rewardsText ? (
            <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono">
              {rewardsText}
            </div>
          ) : rewards ? (
            <div className="flex items-center gap-2">
              <TokenIcon className="w-4 h-4 flex-shrink-0" />
              <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono">
                {rewards}
              </div>
            </div>
          ) : (
            <div className="text-contrast-4 dark:text-white text-sm font-medium font-geist-mono">
              -
            </div>
          )}
        </div>

        {/* Action Column */}
        <div className="flex justify-end">
          {type === 'ongoing' ? (
            <PrimaryButton onClick={handleButtonClick}>{buttonText}</PrimaryButton>
          ) : (
            <SecondaryButton onClick={handleButtonClick}>{buttonText}</SecondaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignRow;
